import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Plus, GripVertical, Trash2, Eye, Settings, Upload, ChevronDown, Type, List, CheckSquare, Square, Save, RotateCcw, ArrowLeft, ExternalLink, EyeOff, Mic } from 'lucide-react';
import server from '../../api/server';
import { useToast } from '../../context/ToastContext';
import { MdArrowBack } from 'react-icons/md';
import ModernTimeDropdownV2 from '../layouts/modernDropdownv2.jsx';

const QuestionEditor = ({
  question,
  index,
  questionTypes,
  selectedQuestionId,
  handleDragStart,
  handleDragOver,
  handleDrop,
  setSelectedQuestionId,
  updateQuestion,
  addOption,
  updateOption,
  removeOption,
  deleteQuestion,
}) => {
  const IconComponent =
    questionTypes.find((t) => t.id === question.type)?.icon || Type;
  const isSelected = selectedQuestionId === question.id;

  return (
    <div
      className={`bg-white rounded-lg border-2 p-6 mb-4 shadow-sm hover:shadow-md transition-all cursor-pointer ${
        isSelected
          ? 'border-indigo-500 shadow-lg ring-2 ring-indigo-200'
          : 'border-gray-200 hover:border-indigo-300'
      }`}
      draggable
      onDragStart={(e) => handleDragStart(e, index)}
      onDragOver={handleDragOver}
      onDrop={(e) => handleDrop(e, index)}
      onClick={() => setSelectedQuestionId(question.id)}
    >
      <div className="flex items-start gap-4">
        <div className="flex-shrink-0 cursor-move text-gray-400 hover:text-gray-600 mt-2">
          <GripVertical size={20} />
        </div>

        <div className="flex-1">
          <div className="flex items-center gap-3 mb-4">
            <div className={`p-2 rounded-lg ${isSelected ? 'bg-indigo-100' : 'bg-indigo-50'}`}>
              <IconComponent size={18} className="text-indigo-600" />
            </div>
            <span className="text-sm font-medium text-gray-700 capitalize">
              {question.type.replace('_', ' ')}
            </span>
            {isSelected && (
              <span className="text-xs bg-indigo-100 text-indigo-700 px-2 py-1 rounded-full font-medium">
                Selected
              </span>
            )}
          </div>

          <div className="space-y-4">
            {/* Title */}
            <div>
              <input
                type="text"
                placeholder="Question title"
                value={question.title}
                onChange={(e) =>
                  updateQuestion(question.id, 'title', e.target.value)
                }
                onClick={(e) => e.stopPropagation()}
                className="w-full text-lg font-medium border-0 border-b-2 border-gray-200 focus:border-indigo-500 outline-none px-0 py-2 bg-transparent"
              />
            </div>

            {/* Description */}
            <div>
              <textarea
                placeholder="Question description (optional)"
                value={question.description}
                onChange={(e) =>
                  updateQuestion(question.id, 'description', e.target.value)
                }
                onClick={(e) => e.stopPropagation()}
                className="w-full text-sm text-gray-600 border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent resize-none"
                rows="2"
              />
            </div>

            {/* Question-specific settings */}
            {question.type === 'text' && (
              <input
                type="text"
                placeholder="Placeholder text"
                value={question.placeholder}
                onChange={(e) =>
                  updateQuestion(question.id, 'placeholder', e.target.value)
                }
                onClick={(e) => e.stopPropagation()}
                className="w-full text-sm border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
              />
            )}

            {(question.type === 'single_select' ||
              question.type === 'multi_select' ||
              question.type === 'dropdown') && (
              <div className="space-y-2">
                <label className="text-sm font-medium text-gray-700">Options</label>
                {question.options.map((option, idx) => (
                  <div key={idx} className="flex items-center gap-2">
                    <input
                      type="text"
                      value={option}
                      onChange={(e) =>
                        updateOption(question.id, idx, e.target.value)
                      }
                      onClick={(e) => e.stopPropagation()}
                      className="flex-1 text-sm border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                      placeholder={`Option ${idx + 1}`}
                    />
                    {question.options.length > 1 && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          removeOption(question.id, idx);
                        }}
                        className="p-1 text-gray-400 hover:text-red-500 transition-colors"
                      >
                        <Trash2 size={16} />
                      </button>
                    )}
                  </div>
                ))}
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    addOption(question.id);
                  }}
                  className="text-sm text-indigo-600 hover:text-indigo-700 font-medium"
                >
                  + Add option
                </button>
              </div>
            )}

            {question.type === 'upload' && (
              <div onClick={(e) => e.stopPropagation()}>
                <label className="text-sm font-medium text-gray-700 block mb-2">
                  Accepted file types
                </label>
                <div className="flex flex-wrap gap-2">
                  {[
                    'image/*',
                    'application/pdf',
                    '.doc,.docx',
                    'video/*',
                    'audio/*',
                  ].map((type) => (
                    <label key={type} className="flex items-center gap-2 text-sm">
                      <input
                        type="checkbox"
                        checked={question.fileTypes.includes(type)}
                        onChange={(e) => {
                          const newTypes = e.target.checked
                            ? [...question.fileTypes, type]
                            : question.fileTypes.filter((t) => t !== type);
                          updateQuestion(question.id, 'fileTypes', newTypes);
                        }}
                        className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                      />
                      <span className="text-gray-600">{type}</span>
                    </label>
                  ))}
                </div>
              </div>
            )}
            {question.type === 'upload' && (
              <div className="mt-4 flex items-center gap-2">
                <label className="text-sm font-medium text-gray-700 flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={question.allowMultiple}
                    onChange={(e) => updateQuestion(question.id, 'allowMultiple', e.target.checked)}
                    className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                  />
                  Allow multiple files
                </label>
              </div>
            )}

            <div className="flex items-center justify-between pt-2">
              <label
                className="flex items-center gap-2 text-sm cursor-pointer"
                onClick={(e) => e.stopPropagation()}
              >
                <input
                  type="checkbox"
                  checked={question.required}
                  onChange={(e) =>
                    updateQuestion(question.id, 'required', e.target.checked)
                  }
                  className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                />
                <span className="text-gray-600">Required</span>
              </label>
            </div>
          </div>
        </div>

        <button
          onClick={(e) => {
            e.stopPropagation();
            deleteQuestion(question.id);
          }}
          className="flex-shrink-0 p-2 text-gray-400 hover:text-red-500 transition-colors"
        >
          <Trash2 size={18} />
        </button>
      </div>
    </div>
  );
};

const FormBuilder = ({ directClientId, directServiceId, directFormId }) => {
  const navigate = useNavigate();
  const { showToast } = useToast();
  const {form_id_param} = useParams();
  
  // Use the direct props (since nested routes are removed)
  const clientId = directClientId;
  const serviceId = directServiceId;
  const formId = directFormId || form_id_param;
  const isEditing = window.location.pathname.includes('edit');

  // Form metadata
  const [formMetadata, setFormMetadata] = useState({
    title: '',
    description: '',
    status: 'draft',
    client_id: clientId || '',
    created_by: '',
    created_at: '',
    updated_at: ''
  });

  // Saved form state (only updated when Save Form is clicked)
  const [savedForm, setSavedForm] = useState({
    title: '',
    description: '',
    questions: []
  });

  // Draft form state (working copy)
  const [draftForm, setDraftForm] = useState({
    title: '',
    description: '',
    questions: []
  });
  
  const [previewMode, setPreviewMode] = useState(false);
  const [draggedItem, setDraggedItem] = useState(null);
  const [selectedQuestionId, setSelectedQuestionId] = useState(null);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  
  // Client selection state
  const [clientIdOptions, setClientIdOptions] = useState([]);
  const [selectedClientId, setSelectedClientId] = useState(clientId || '');
  const [isAdmin, setIsAdmin] = useState(false);

  const questionTypes = [
    { id: 'text', label: 'Text', icon: Type },
    { id: 'textarea', label: 'Textarea', icon: Type },
    { id: 'single_select', label: 'Single Select', icon: Square },
    { id: 'multi_select', label: 'Multi Select', icon: CheckSquare },
    { id: 'dropdown', label: 'Dropdown', icon: ChevronDown },
    { id: 'upload', label: 'File Upload', icon: Upload },
    { id: 'audio_recording', label: 'Audio Recording', icon: Mic }
  ];

  // Get user email from cookies
  const getUserEmail = () => {
    const userCookie = document.cookie
      .split('; ')
      .find(row => row.startsWith('dashboard_login_user_creds='));
    
    if (userCookie) {
      try {
        const userData = JSON.parse(decodeURIComponent(userCookie.split('=')[1]));
        return userData.email;
      } catch (error) {
        console.error('Error parsing user data:', error);
        return null;
      }
    }
    return null;
  };

  // Get user role from cookies
  const getUserRole = () => {
    const userCookie = document.cookie
      .split('; ')
      .find(row => row.startsWith('dashboard_login_user_creds='));
    
    if (userCookie) {
      try {
        const userData = JSON.parse(decodeURIComponent(userCookie.split('=')[1]));
        return userData.role || 'admin';
      } catch (error) {
        console.error('Error parsing user data:', error);
        return 'admin';
      }
    }
    return 'admin';
  };

  // Get all clients for admin users
  const getAllClients = async () => {
    try {
      const userCookie = document.cookie
        .split('; ')
        .find(row => row.startsWith('dashboard_login_user_creds='));
      
      if (!userCookie) return;
      
      const user = JSON.parse(decodeURIComponent(userCookie.split('=')[1]));
      const email = user.email;
      const role = user.role || 'admin';
      
      const response = await server.get_all_clients(clientId, serviceId, email, role, 'forms', undefined);
      
      if (response.status === 200) {
        const data = await response.json();
        const clientOptions = [];
        
        for (let i = 0; i < data.data.length; i++) {
          clientOptions.push(data.data[i][0]);
        }
        
        setClientIdOptions(clientOptions);
        
        // Set admin status based on number of clients
        if (clientOptions.length > 1) {
          setIsAdmin(true);
        } else {
          setIsAdmin(false);
        }
      }
    } catch (error) {
      console.error('Error fetching clients:', error);
    }
  };

  // Handle client selection change
  const handleClientIdChange = (e) => {
    const newClientId = e.target.value;
    setSelectedClientId(newClientId);
    setFormMetadata(prev => ({ ...prev, client_id: newClientId }));
    setHasUnsavedChanges(true);
  };

  // Load existing form data if editing
  useEffect(() => {
    if (formId) {
      loadFormData();
    }
  }, [formId]);

  // Load client options on component mount
  useEffect(() => {
    const role = getUserRole();
    if (role) {
      getAllClients();
    }
  }, []);

  // Initialize selectedClientId when clientId changes
  useEffect(() => {
    if (clientId && !selectedClientId) {
      setSelectedClientId(clientId);
    }
  }, [clientId, selectedClientId]);
  const handleUnpublishForm = async () => {
    const email = getUserEmail();
    const response = await server.updateFormStatus(formId, 'inactive', email, clientId, serviceId);
    if (response.status === 200) {
      setFormMetadata(prev => ({ ...prev, status: 'inactive' }));
      showToast('Form unpublished successfully!', 'success');
    }else{
      showToast('Failed to unpublish form', 'error');
    }
  };

  const loadFormData = async () => {
    try {
      setLoading(true);
      const email = getUserEmail();
      const response = await server.getFormDetails(clientId, serviceId, formId, email, 'forms', undefined);
      
      if (response.status === 200) {
        const data = await response.json();
        const formData = data.data;
        
        setFormMetadata({
          title: formData.title || '',
          description: formData.description || '',
          status: formData.status || 'draft',
          client_id: formData.client_id || clientId,
          created_by: formData.created_by || '',
          created_at: formData.created_at || '',
          updated_at: formData.updated_at || ''
        });

        // Set the selected client ID for the dropdown
        setSelectedClientId(formData.client_id || clientId);

        const formContent = {
          title: formData.title || '',
          description: formData.description || '',
          questions: formData.questions || []
        };

        setSavedForm(formContent);
        setDraftForm(formContent);
      } else {
        showToast('Failed to load form data', 'error');
        navigate('/forms');
      }
    } catch (error) {
      console.error('Error loading form data:', error);
      showToast('Error loading form data', 'error');
      navigate('/forms');
    } finally {
      setLoading(false);
    }
  };

  const updateDraftForm = (updater) => {
    setDraftForm(updater);
    setHasUnsavedChanges(true);
  };

  const addQuestion = (type) => {
    const newQuestion = {
      id: Date.now(),
      type,
      title: '',
      description: '',
      required: false,
      options: type === 'single_select' || type === 'multi_select' || type === 'dropdown' ? ['Option 1'] : [],
      placeholder: type === 'text' ? 'Enter your answer...' : '',
      fileTypes: type === 'upload' ? ['image/*', 'application/pdf'] : [],
      allowMultiple: false
    };
    
    updateDraftForm(prev => ({
      ...prev,
      questions: [...prev.questions, newQuestion]
    }));
    
    // Auto-select the newly created question
    setSelectedQuestionId(newQuestion.id);
  };

  const updateQuestion = (id, field, value) => {
    updateDraftForm(prev => ({
      ...prev,
      questions: prev.questions.map(q => 
        q.id === id ? { ...q, [field]: value } : q
      )
    }));
    // Maintain selection after update
    setSelectedQuestionId(id);
  };

  const deleteQuestion = (id) => {
    updateDraftForm(prev => ({
      ...prev,
      questions: prev.questions.filter(q => q.id !== id)
    }));
    
    // Clear selection if deleted question was selected
    if (selectedQuestionId === id) {
      setSelectedQuestionId(null);
    }
  };

  const addOption = (questionId) => {
    updateDraftForm(prev => ({
      ...prev,
      questions: prev.questions.map(q => 
        q.id === questionId 
          ? { ...q, options: [...q.options, `Option ${q.options.length + 1}`] }
          : q
      )
    }));
    // Maintain selection after adding option
    setSelectedQuestionId(questionId);
  };

  const updateOption = (questionId, optionIndex, value) => {
    updateDraftForm(prev => ({
      ...prev,
      questions: prev.questions.map(q => 
        q.id === questionId 
          ? { 
              ...q, 
              options: q.options.map((opt, idx) => idx === optionIndex ? value : opt)
            }
          : q
      )
    }));
    // Maintain selection after updating option
    setSelectedQuestionId(questionId);
  };

  const removeOption = (questionId, optionIndex) => {
    updateDraftForm(prev => ({
      ...prev,
      questions: prev.questions.map(q => 
        q.id === questionId 
          ? { ...q, options: q.options.filter((_, idx) => idx !== optionIndex) }
          : q
      )
    }));
    // Maintain selection after removing option
    setSelectedQuestionId(questionId);
  };

  const handleDragStart = (e, index) => {
    setDraggedItem(index);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDrop = (e, dropIndex) => {
    e.preventDefault();
    if (draggedItem === null) return;

    const newQuestions = [...draftForm.questions];
    const draggedQuestion = newQuestions[draggedItem];
    newQuestions.splice(draggedItem, 1);
    newQuestions.splice(dropIndex, 0, draggedQuestion);

    updateDraftForm(prev => ({ ...prev, questions: newQuestions }));
    setDraggedItem(null);
    // Maintain selection after reordering
    setSelectedQuestionId(draggedQuestion.id);
  };

  const saveForm = async () => {
    try {
      setSaving(true);
      const email = getUserEmail();
      
      if (!draftForm.title.trim()) {
        showToast('Please enter a form title', 'error');
        return;
      }

      let formData = {
        title: draftForm.title,
        description: draftForm.description,
        questions: draftForm.questions,
        status: formMetadata.status,
        client_id: selectedClientId || clientId,
        email: email,
        menu: 'forms',
        submenu: undefined
      };

      let response;
      if (isEditing) {
        formData.form_id = formId;
        response = await server.updateForm(formData);
      } else {
        response = await server.createForm(formData);
      }

      if (response.status === 200) {
        setSavedForm({ ...draftForm });
        setHasUnsavedChanges(false);
        showToast(isEditing ? 'Form updated successfully!' : 'Form created successfully!', 'success');
      }
    } catch (error) {
      console.error('Error saving form:', error);
      showToast('Error saving form', 'error');
    } finally {
      setSaving(false);
    }
  };

  const discardChanges = () => {
    setDraftForm({ ...savedForm });
    setHasUnsavedChanges(false);
    setSelectedQuestionId(null);
  };

  const handlePublishForm = async () => {
    try {
      setSaving(true);
      const email = getUserEmail();
      
      const response = await server.updateFormStatus(formId, 'active', email, clientId, serviceId);
      
      if (response.status === 200) {
        setFormMetadata(prev => ({ ...prev, status: 'active' }));
        showToast('Form published successfully!', 'success');
      } else {
        showToast('Failed to publish form', 'error');
      }
    } catch (error) {
      console.error('Error publishing form:', error);
      showToast('Error publishing form', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleGeneratePublicLink = () => {
    if (!formId) {
      showToast('Please save the form first', 'error');
      return;
    }
    
    const publicUrl = `${window.location.origin}/public/forms/${formId}`;
    window.open(publicUrl, '_blank');
  };

  const FormPreview = () => {
    const [responses, setResponses] = useState({});

    const handleResponse = (questionId, value) => {
      setResponses(prev => ({ ...prev, [questionId]: value }));
    };

    const formToPreview = hasUnsavedChanges ? draftForm : savedForm;

    return (
      <div className="max-w-2xl mx-auto bg-white rounded-lg shadow-lg p-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-4">
            {formToPreview.title || 'Untitled Form'}
          </h1>
          {formToPreview.description && (
            <p className="text-gray-600 text-lg leading-relaxed">
              {formToPreview.description}
            </p>
          )}
        </div>

        <div className="space-y-8">
          {formToPreview.questions.map((question) => (
            <div key={question.id} className="border-l-4 border-indigo-500 pl-6">
              <div className="mb-4">
                <h3 className="text-lg font-semibold text-gray-900">
                  {question.title || 'Untitled Question'}
                  {question.required && <span className="text-red-500 ml-1">*</span>}
                </h3>
                {question.description && (
                  <p className="text-gray-600 mt-1">{question.description}</p>
                )}
              </div>

              <div className="mb-6">
                {question.type === 'text' && (
                  <input
                    type="text"
                    placeholder={question.placeholder}
                    className="w-full border border-gray-300 rounded-lg px-4 py-3 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                    onChange={(e) => handleResponse(question.id, e.target.value)}
                  />
                )}
                {question.type === 'textarea' && (
                  <textarea
                    placeholder={question.placeholder}
                    className="w-full border border-gray-300 rounded-lg px-4 py-3 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent resize-none"
                    rows="3"
                    onChange={(e) => handleResponse(question.id, e.target.value)}
                  />
                )}

                {question.type === 'single_select' && (
                  <div className="space-y-3">
                    {question.options.map((option, idx) => (
                      <label key={idx} className="flex items-center gap-3 cursor-pointer">
                        <input
                          type="radio"
                          name={`question_${question.id}`}
                          value={option}
                          onChange={(e) => handleResponse(question.id, e.target.value)}
                          className="w-4 h-4 text-indigo-600 border-gray-300 focus:ring-indigo-500"
                        />
                        <span className="text-gray-700">{option}</span>
                      </label>
                    ))}
                  </div>
                )}

                {question.type === 'multi_select' && (
                  <div className="space-y-3">
                    {question.options.map((option, idx) => (
                      <label key={idx} className="flex items-center gap-3 cursor-pointer">
                        <input
                          type="checkbox"
                          value={option}
                          onChange={(e) => {
                            const currentValues = responses[question.id] || [];
                            const newValues = e.target.checked
                              ? [...currentValues, option]
                              : currentValues.filter(v => v !== option);
                            handleResponse(question.id, newValues);
                          }}
                          className="w-4 h-4 text-indigo-600 border-gray-300 rounded focus:ring-indigo-500"
                        />
                        <span className="text-gray-700">{option}</span>
                      </label>
                    ))}
                  </div>
                )}

                {question.type === 'dropdown' && (
                  <select
                    onChange={(e) => handleResponse(question.id, e.target.value)}
                    className="w-full border border-gray-300 rounded-lg px-4 py-3 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent bg-white"
                  >
                    <option value="">Select an option...</option>
                    {question.options.map((option, idx) => (
                      <option key={idx} value={option}>{option}</option>
                    ))}
                  </select>
                )}

                {question.type === 'upload' && (
                  <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center hover:border-indigo-400 transition-colors">
                    <Upload className="mx-auto h-12 w-12 text-gray-400 mb-4" />
                    <p className="text-gray-600 mb-2">Click to upload or drag and drop</p>
                    <p className="text-xs text-gray-500">
                      Supported formats: {question.fileTypes.join(', ')}
                      {question.allowMultiple && <span className="block mt-1">Maximum 20 files allowed</span>}
                    </p>
                    <input
                      type="file"
                      accept={question.fileTypes.join(',')}
                      className="hidden"
                      multiple={question.allowMultiple}
                    />
                  </div>
                )}

                {question.type === 'audio_recording' && (
                  <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center">
                    <Mic className="mx-auto h-10 w-10 text-gray-400 mb-3" />
                    <p className="text-gray-600 mb-1">Start recording to capture audio</p>
                    <p className="text-xs text-gray-500">
                      On public form: user can start, see duration, stop, then play/download before submitting.
                    </p>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>

        <div className="mt-8 pt-6 border-t">
          <button className="bg-indigo-600 hover:bg-indigo-700 text-white px-8 py-3 rounded-lg font-medium transition-colors">
            Submit
          </button>
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  if (previewMode) {
    return (
      <div className="w-full py-8">
        <div className="w-full px-4">
          <div className="mb-6 flex items-center justify-between">
            <button
              onClick={() => setPreviewMode(false)}
              className="flex items-center gap-2 text-indigo-600 hover:text-indigo-700 font-medium"
            >
              <Settings size={18} />
              Back to Editor
            </button>
            {hasUnsavedChanges && (
              <div className="text-amber-600 text-sm font-medium">
                ⚠️ You have unsaved changes. Preview shows your working copy.
              </div>
            )}
          </div>
          <FormPreview />
        </div>
      </div>
    );
  }

  return (
    <div className="w-full">
      <div className="flex items-center gap-2">
        <button onClick={() => navigate('/forms')} style={{display: 'flex',alignItems: 'center',color: '#4b5563',cursor: 'pointer',border: 'none',background: 'none',padding: '8px',fontSize: '14px',}}>
          <MdArrowBack style={{ marginRight: '8px' }} />
          Back
        </button>
      </div>
      <div className="bg-white border-b shadow-sm">
        <div className="w-full px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              {/* <button
                onClick={() => navigate('/forms')}
                className="flex items-center gap-2 text-gray-600 hover:text-gray-900 font-medium"
              >
                <ArrowLeft size={18} />
                Back to Forms
              </button> */}
              <div className="border-l border-gray-300 pl-4">
                <h1 className="text-2xl font-bold text-gray-900">
                  {isEditing ? 'Edit Form' : 'Create New Form'}
                </h1>
                <p className="text-gray-600 mt-1">
                  {isEditing ? 'Modify your existing form' : 'Create custom forms for your clients'}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <button
                onClick={() => setPreviewMode(true)}
                className="flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
              >
                <Eye size={18} />
                Preview
              </button>
              
              {formId && (
                <button
                  onClick={handleGeneratePublicLink} disabled={formMetadata.status === 'inactive' || formMetadata.status === 'draft'}
                  className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors ${formMetadata.status === 'inactive' || formMetadata.status === 'draft' ? 'bg-gray-100 text-gray-500 cursor-not-allowed' : 'hover:bg-gray-50 border border-gray-300'}`}
                >
                  <ExternalLink size={18} />
                  Public Link
                </button>
              )}

              {isEditing && (formMetadata.status === 'draft' || formMetadata.status === 'inactive') && (
                <button
                  onClick={handlePublishForm}
                  disabled={saving}
                  className="flex items-center gap-2 px-4 py-2 bg-green-50 hover:bg-green-100 text-green-600 rounded-lg font-medium transition-colors disabled:opacity-50"
                >
                  {saving ? 'Publishing...' : 'Publish Form'}
                </button>
              )}
              {
                isEditing && formMetadata.status === 'active' && (
                  <button
                    onClick={handleUnpublishForm}
                    disabled={saving}
                    className="flex items-center gap-2 px-4 py-2 bg-red-50 hover:bg-red-100 text-red-600 rounded-lg font-medium transition-colors disabled:opacity-50"
                  >
                    <EyeOff size={18} />
                    Unpublish
                  </button>
                )
              }
              
              {hasUnsavedChanges && (
                <button
                  onClick={discardChanges}
                  className="flex items-center gap-2 px-4 py-2 border border-gray-300 text-gray-600 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  <RotateCcw size={18} />
                  Discard Changes
                </button>
              )}
              
              <button 
                onClick={saveForm}
                disabled={saving}
                className={`flex items-center gap-2 px-6 py-2 rounded-lg font-medium transition-colors ${
                  hasUnsavedChanges 
                    ? 'bg-indigo-600 hover:bg-indigo-700 text-white' 
                    : 'bg-gray-100 text-gray-500 cursor-not-allowed'
                }`}
              >
                <Save size={18} />
                {saving ? 'Saving...' : 'Save Form'}
              </button>
            </div>
          </div>
          
          {hasUnsavedChanges && (
            <div className="mt-3 p-3 bg-amber-50 border border-amber-200 rounded-lg">
              <p className="text-sm text-amber-800">
                <strong>Unsaved changes:</strong> Your form has unsaved changes. Click "Save Form" to save your work.
              </p>
            </div>
          )}
        </div>
      </div>

      <div className="w-full px-4 py-8">
        {/* Client ID Selection Box - Separate from form content */}
        {((isAdmin && clientIdOptions.length > 0) || selectedClientId) && (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
            {/* Client Selection Dropdown - Only show during form creation */}
            {!isEditing && isAdmin && clientIdOptions.length > 0 && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Select Client ID
                </label>
                <ModernTimeDropdownV2 
                  placeholder="Select Client ID" 
                  value={selectedClientId} 
                  onChange={handleClientIdChange} 
                  options={clientIdOptions}
                  width="w-64"
                />
              </div>
            )}
            {/* Show selected client ID (read-only) during editing or for non-admin users */}
            {(isEditing || !isAdmin) && selectedClientId && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Client ID
                </label>
                <div className="px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg text-gray-700 font-medium">
                  {selectedClientId}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Form Title and Description */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8 mb-8">
          <div className="space-y-6">
            <div>
              <input
                type="text"
                placeholder="Form Title"
                value={draftForm.title}
                onChange={(e) => updateDraftForm(prev => ({ ...prev, title: e.target.value }))}
                className="w-full text-3xl font-bold border-0 border-b-2 border-gray-200 focus:border-indigo-500 outline-none px-0 py-3 bg-transparent placeholder-gray-400"
              />
            </div>
            <div>
              <textarea
                placeholder="Form description (optional)"
                value={draftForm.description}
                onChange={(e) => updateDraftForm(prev => ({ ...prev, description: e.target.value }))}
                className="w-full text-lg text-gray-600 border border-gray-200 rounded-lg px-4 py-3 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent resize-none placeholder-gray-400"
                rows="3"
              />
            </div>
          </div>
        </div>

        <div className="space-y-6">
          {draftForm.questions.map((question, index) => (
            <QuestionEditor
              key={question.id}
              question={question}
              index={index}
              questionTypes={questionTypes}
              selectedQuestionId={selectedQuestionId}
              handleDragStart={handleDragStart}
              handleDragOver={handleDragOver}
              handleDrop={handleDrop}
              setSelectedQuestionId={setSelectedQuestionId}
              updateQuestion={updateQuestion}
              addOption={addOption}
              updateOption={updateOption}
              removeOption={removeOption}
              deleteQuestion={deleteQuestion}
            />
          ))}
        </div>

        <div className="mt-8">
          <div className="bg-white rounded-lg border-2 border-dashed border-gray-300 p-8">
            <div className="text-center">
              <Plus className="mx-auto h-12 w-12 text-gray-400 mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-4">Add a Question</h3>
              <div className="flex flex-wrap justify-center gap-3">
                {questionTypes.map((type) => {
                  const IconComponent = type.icon;
                  return (
                    <button
                      key={type.id}
                      onClick={() => addQuestion(type.id)}
                      className="flex items-center gap-2 px-4 py-3 bg-indigo-50 hover:bg-indigo-100 text-indigo-700 rounded-lg border border-indigo-200 transition-colors"
                    >
                      <IconComponent size={18} />
                      <span className="font-medium">{type.label}</span>
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FormBuilder; 